import { Identifiable } from "../core";

/** What a template declares it needs — produced by the engine's scanner. */
export interface ITemplateManifest {
  slides: number;
  /** Slides that will appear in the PDF — `slides` minus the hidden ones. */
  visible_slides?: number;
  /** Slides hidden in PowerPoint: filled, but never exported. */
  hidden_slides?: number[];
  /**
   * PowerPoint's id for each slide, in deck order (`slide_ids[0]` is slide 1).
   * Stable across edits, which positions are not. Absent on templates scanned
   * before it was reported; re-checking the template adds it.
   */
  slide_ids?: number[];
  /** Page size in EMU; absent on templates scanned before it was reported. */
  slide_size?: { width: number; height: number } | null;
  tokens: string[];
  image_slots: string[];
  canvas_slots: string[];
  /** Token prefixes whose block the engine removes when the payload is empty. */
  optional_slots?: string[];
  token_slides: Record<string, number[]>;
}

export interface IValidationProblem {
  /**
   * `undeclared_copy` is not a typo the way `unknown_token` is: the `copy.*`
   * namespace is open, so a key nobody has seen before is the normal case. It
   * is still a problem until somebody says what the field is for — an
   * undescribed field reaches a writer with no guidance and the co-writer with
   * no prompt. The import step offers a form for these, not a spelling fix.
   */
  kind: "unknown_token" | "unregistered_canvas" | "undeclared_copy";
  name: string;
  slides: number[];
  suggestion: string | null;
}

export interface IValidation {
  valid: boolean;
  problems: IValidationProblem[];
  resolvable_tokens: number;
  /** Every copy list root the deck draws, declared or not. */
  copy_fields?: string[];
  copy_lists?: Record<string, ICopyListShape>;
}

/** Who may write a copy field, and whether it holds up generation. */
export type CopyFieldAi = "draft" | "never";
export type CopyOrigin = "ai_draft" | "human" | "translated";

/**
 * How many values a declaration stands for.
 *
 * `fixed` — the count never varies. Six feature bullets are always six.
 * `range` — the count is a property of the listing. A floor-plan block holds
 * one to four items, and the slots it does not use are removed from the deck
 * rather than rendered blank: an unfilled item is a star bullet beside two
 * empty lines, not nothing.
 */
export type CopyFieldKind = "scalar" | "fixed" | "range";

/** One part of an item. A plain-string list has none. */
export interface ICopyPart {
  name: string;
  max_chars?: number | null;
}

/**
 * The declaration of one `copy.*` **list**.
 *
 * Global, not scoped to a template: templates that draw the same slide share
 * the copy on it — written once, used by all of them. And one
 * declaration covers every item in the list, because the items do not have
 * separate purposes: `copy.floorplan.0.body` and `.2.body` are the same field
 * asked twice.
 */
export interface ICopyField extends Identifiable {
  /** The list root — `copy.floorplan`, never `copy.floorplan.0.title`. */
  key: string;
  label: string;
  /** What the list is for. Also the co-writer's prompt. */
  intent: string;
  kind: CopyFieldKind;
  /** `[]` for a plain-string list, `[title, body]` for an object list. */
  parts: ICopyPart[];
  /**
   * How many items must be written before a brochure may be generated. The
   * *maximum* is not here — declarations are global and two decks may draw
   * different counts of the same list, so the ceiling comes from the manifest.
   */
  min_items: number;
  ai: CopyFieldAi;
  used_by: {
    id: number; name: string; namespace: string; language: string;
  }[];
}

/** What a deck asks of one list, read from its manifest. */
export interface ICopyListShape {
  slots: number;
  parts: string[];
  /** Slot indexes the engine can drop when empty. */
  optional: number[];
}

/** One item of a list in the editor, with its values in every language. */
export interface ICopyEditorItem {
  index: number;
  /** False means the deck cannot drop this slot, so it has to be filled. */
  droppable: boolean;
  /** language -> token -> text */
  values: Record<string, Record<string, string>>;
  origins: Record<string, Record<string, CopyOrigin>>;
}

export interface ICopyEditorList {
  root: string;
  label: string;
  intent: string;
  kind: CopyFieldKind | null;
  ai: CopyFieldAi | null;
  min_items: number;
  /** What the deck has room for. */
  slots: number;
  parts: ICopyPart[];
  declared: boolean;
  /** A single text rather than a list: one item, stored under the root itself. */
  scalar?: boolean;
  items: ICopyEditorItem[];
}

export interface ICopyEditorState {
  /** Every language, so the editor can offer translating from the others. */
  languages: string[];
  /** The template's language, which the editor shows; absent for a development. */
  template_language?: string | null;
  lists: ICopyEditorList[];
}

/** What one translation did, per list root. */
export interface ICopyTranslateReport {
  /** Items written from the other language. */
  translated: Record<string, number>;
  /** Items left as they were, because a person wrote them. */
  kept: Record<string, number>;
}

/** What one co-writer run did, per list root. */
export interface ICopyDraftReport {
  /** How many items each list got. */
  drafted: Record<string, number>;
  /** Lists that came back with fewer items than the gate needs. */
  short: string[];
  /** Lists left out, and why. */
  skipped: Record<string, "undescribed" | "a_person_writes_it" | "already_written">;
}

/** Why one copy list is holding up, or merely flagged on, a generation. */
export interface ICopyProblem {
  root: string;
  label: string;
  intent: string;
  /**
   * `too_few` — fewer items than `min_items`. `gap` — a slot the deck cannot
   * drop was left empty. `undeclared` — nobody has described the list.
   * `draft` — all there, but the co-writer wrote some of it. Only `draft` is
   * non-blocking.
   */
  reason: "too_few" | "gap" | "undeclared" | "draft";
  blocking: boolean;
  written: number;
  min_items: number;
  drafts?: number;
  slots?: number[];
}

export interface ICheckResult {
  manifest: ITemplateManifest;
  validation: IValidation;
}

export type TemplateScope = "listing" | "destination" | "default";

export interface IPresentationTemplate extends Identifiable {
  name: string;
  namespace: string;
  language: "es" | "en";
  listings: number[];
  destination: number | null;
  destination_name?: string;
  scope: TemplateScope;
  file: string;
  is_active: boolean;
  /** Whether this template's PDFs are sent to the sales team's Drive folder. */
  publishes_to_drive: boolean;
  manifest: ITemplateManifest;
  validated_at: string | null;
  token_count: number;
  image_slot_count: number;
  /**
   * Whether the deck declares an `image_floorplan` slot. The drawer gates its
   * floor-plan prompt on this, so nobody is interrupted over a missing plan
   * that would not change the PDF either way.
   */
  declares_floor_plan: boolean;
  /** The copy list roots this deck draws. Empty on every older template. */
  copy_field_keys: string[];
  /** Of those, the ones nobody has described yet. */
  undeclared_copy_keys: string[];
  /** What the deck asks of each list — slot count and which slots it can drop. */
  copy_lists: Record<string, ICopyListShape>;
  /**
   * The slides the teaser keeps, by PowerPoint slide id. Empty: no teaser.
   * Every brochure from this template is cut down to these into its teaser.
   */
  teaser_slide_ids: number[];
  /** Those slides as numbers in the current deck, and how many it no longer has. */
  teaser_slides: { numbers: number[]; missing: number };
  slide_count: number;
  /** How many properties have an output from this template. */
  presentation_count: number;
  /** How many of those are stale or failed — a bare total hides both. */
  attention_count: number;
  /** Slides hidden in PowerPoint, so the PDF has fewer pages than the deck. */
  hidden_slide_count: number;
  /** Signed URL of the slide-1 preview, or null when none could be rendered. */
  thumbnail_url: string | null;
  /** Signed download URL for the template .pptx itself. */
  file_url: string | null;
  /** Signed URL of the full-deck preview PDF; null until one has been built. */
  preview_url: string | null;
  /** width / height of the deck's page. Below 1 is portrait. */
  aspect: number | null;
  created_at: string;
  modified_at: string;
}

export type PresentationStatus =
  | "pending" | "ready" | "pinned" | "stale" | "failed"
  /** Refused before rendering: the property is missing something. Not an error. */
  | "needs_info";

export interface IPresentation extends Identifiable {
  listing: number;
  listing_name?: string;
  template: number;
  template_name?: string;
  status: PresentationStatus;
  status_label: string;
  error: string;
  pptx_file: string;
  pdf_file: string;
  pptx_url: string | null;
  pdf_url: string | null;
  generated_at: string | null;
  pinned_at: string | null;
  created_at: string;
  modified_at: string;
  /** The Drive copy of the PDF; blank until first sent. */
  drive_file_id: string;
  drive_file_name: string;
  drive_synced_at: string | null;
  /** Which generation Drive holds — older than `generated_at` means unsent. */
  drive_generated_at: string | null;
  drive_error: string;
  /** On a teaser, the brochure it was cut from. */
  parent: number | null;
  is_teaser: boolean;
  /** A brochure's teaser; null on a teaser, or when the template keeps none. */
  teaser: ITeaser | null;
}

/** The teaser riding on its brochure: enough to show and open it. */
export interface ITeaser {
  id: number;
  status: PresentationStatus;
  status_label: string;
  error: string;
  generated_at: string | null;
  pptx_url: string | null;
  pdf_url: string | null;
}

/** One gap between what a template needs and what the listing supplies. */
export interface IMissingField {
  label: string;
  paths: string[];
  source: "listing" | "unit" | "destination" | "development";
  count: number;
  detail: string | null;
}

export interface IReadiness {
  /**
   * Whether the *listing's own data* is complete. Deliberately unchanged by
   * copy fields, so counts do not shift under a template that gained some.
   * Whether generation may proceed is `blocked`.
   */
  ready: boolean;
  required: number;
  resolved: number;
  missing: IMissingField[];
  /** Every copy field with something to say, blocking ones first. */
  copy?: ICopyProblem[];
  /** The hard gate: generation refuses while this is true. */
  blocked?: boolean;
  blocking_copy?: number;
  /** Listing shapes the template draws no layout for (a third storey, a fifth plan). */
  blocking_layouts?: number;
  /** The amenity grid has under a row of captioned photos. */
  blocking_photos?: number;
  copy_drafts?: number;
}

export interface IMaterialRow {
  namespace: string;
  language: "es" | "en";
  template: { id: number; name: string; scope: TemplateScope } | null;
  template_reason: string;
  presentation: IPresentation | null;
  readiness: IReadiness | null;
  /** The same checklist the template drawer gates Generate on. */
  checklist: IChecklist | null;
}

export interface IListingMaterials {
  /** Enough to open the Prepare screen (`ITemplateRow["listing"]`) from here. */
  listing: {
    id: number;
    name: string;
    slug: string;
    destination: string | null;
    published: boolean;
    unit: number | null;
    design: number | null;
  };
  materials: IMaterialRow[];
}

export type CoverageState =
  PresentationStatus | "not_generated" | "no_template";

/** One property inside a template's drawer. */
export interface ITemplateRow {
  listing: {
    id: number;
    /** The listings endpoint is addressed by slug; opens the edit wizard. */
    slug: string;
    name: string;
    destination: string | null;
    /** The drawer hides unpublished properties unless asked to show them. */
    published: boolean;
    /** For the Adjust menu — the floor-plan wizard is addressed by design. */
    unit: number | null;
    design: number | null;
    /** Every storey the template draws a plan for has one (unit override first). */
    has_floor_plan: boolean;
    /** The storeys the template draws a plan for that have none published. */
    missing_floor_plans?: { index: number; level: number | null; name: string }[];
  };
  presentation: IPresentation | null;
  /**
   * What this property still owes the template in authored copy. `blocking`
   * refuses generation; `drafts` only means the co-writer's text has not been
   * reviewed by anyone.
   */
  copy: { blocking: number; drafts: number };
  /**
   * The checklist steps known to be open, from checks cheap enough for the
   * whole table: copy, floor plans, the amenity grid. Labels. Empty does not
   * mean ready — gallery, map and property details are only checked by the
   * full checklist (`IChecklist`), which Generate always asks for first.
   */
  todo?: { blocking: string[]; review: string[] };
  /**
   * False when the property holds an output from this template but would now
   * resolve to a different one — a brochure orphaned by a newer, more specific
   * template. It still exists, so it is still shown.
   */
  in_audience: boolean;
}

export interface ITemplateDetail {
  template: IPresentationTemplate;
  audience_count: number;
  rows: ITemplateRow[];
}

/** Plain-language names for material types. */
export const NAMESPACE_LABELS: Record<string, string> = {
  brochure: "Brochure",
  sip: "Interactive presentation",
};

/**
 * How each state is shown. The database says PINNED; nobody outside engineering
 * knows what that means, so the screen never shows it.
 */
export const STATE_LABELS: Record<CoverageState, string> = {
  pending: "Generating…",
  ready: "Up to date",
  pinned: "Customized",
  stale: "Needs refresh",
  failed: "Failed",
  needs_info: "Needs info",
  not_generated: "Not generated",
  no_template: "No template",
};

export const STATE_COLORS: Record<CoverageState, string> = {
  pending: "processing",
  ready: "success",
  pinned: "blue",
  stale: "warning",
  failed: "error",
  needs_info: "warning",
  not_generated: "default",
  no_template: "default",
};

/** Where a missing value is fixed — turns a gap into a place to go. */
export const SOURCE_LABELS: Record<IMissingField["source"], string> = {
  listing: "on this listing",
  unit: "on the unit",
  destination: "on the destination",
  development: "on the development",
};

/**
 * The location map on a brochure's location slide.
 *
 * A map is a pure function of its parameters, so there is no version history
 * here and nothing to publish: the stored settings *are* the map, and the image
 * is regenerated from them on demand.
 */
export interface IMapPreview {
  /** Null when the listing has no coordinates, or the vendor call failed. */
  url: string | null;
  /** What is persisted today, so the UI can mark unsaved experiments. */
  saved: { zoom: number; map_type: string };
  has_custom_image: boolean;
  has_coordinates: boolean;
}

export const MAP_TYPES = ['roadmap', 'satellite'] as const;

/** One template's box for a gallery slot — where it sits and what shape it is. */
export interface IGallerySlotBox {
  template: number;
  template_name: string;
  namespace: string;
  language: string;
  /** Null for a template scanned before the engine reported boxes. */
  slide: number | null;
  aspect: number | null;
}

export interface IGallerySlot {
  slot: string;
  boxes: IGallerySlotBox[];
  /** Two templates draw this slot at different shapes, so one pick cannot suit both. */
  conflict: boolean;
}

export interface IGalleryCandidate {
  /** `gallery:12` or `destination:cover_image` — the handle a pick is saved as. */
  ref: string;
  source: "listing" | "unit" | "design" | "destination";
  gallery: string;
  name: string;
  url: string;
  thumbnail: string;
}

export interface IGalleryPicker {
  slots: IGallerySlot[];
  candidates: IGalleryCandidate[];
  /** Slots a person pinned: slot -> ref. */
  picks: Record<string, string>;
  /** The refs unpicked slots are filled from, in order, skipping picked ones. */
  default_order: string[];
  /** The amenities slide's photo grid, `image_amenity_0`…; empty when no template draws it. */
  amenity_slots: IGallerySlot[];
  /** Amenity cells a person filled. Only picks fill them — there is no default. */
  amenity_picks: Record<string, IAmenityPick>;
  /** Cells per row. A row prints whole or not at all. */
  amenity_row: number;
  caption_max: number;
  languages: string[];
}

/** One cell of the amenity grid: its photo and a caption per language. */
export interface IAmenityPick {
  ref: string;
  captions: Record<string, string>;
}

/** Where Drive stands for one presentation. */
export type DriveState = "sent" | "waiting" | "failed" | "off";

export type DriveFolderStatus = "suggested" | "not_found" | "confirmed" | "disabled";

export interface IPresentationEvent {
  id: number;
  kind: "rendered" | "render_failed" | "sent" | "send_failed" | "changed";
  kind_label: string;
  created_at: string;
  /** Kind-specific: Drive action and link for `sent`, `error` for failures. */
  detail: Record<string, any>;
}

/** One row of the monitoring view. */
export interface IMonitorRow {
  id: number;
  listing: number;
  listing_name: string;
  template: {
    id: number;
    name: string;
    namespace: string;
    language: "es" | "en";
    is_active: boolean;
    publishes_to_drive: boolean;
  };
  /** A brochure's teaser, listed straight after it. */
  is_teaser: boolean;
  status: PresentationStatus;
  status_label: string;
  error: string;
  pptx_url: string | null;
  pdf_url: string | null;
  generated_at: string | null;
  drive: {
    state: DriveState;
    /** Why it is `off`, or Drive's message when `failed`. */
    reason: string | null;
    folder_status: DriveFolderStatus | null;
    folder_name: string;
    file_name: string;
    link: string | null;
    synced_at: string | null;
  };
  last_event: IPresentationEvent | null;
}

export interface IDriveFolderRow {
  listing: { id: number; name: string };
  status: DriveFolderStatus;
  folder_id: string;
  folder_name: string;
  /** How discovery matched it: "exact", "prefix", or "" when a person chose. */
  match: string;
  confirmed_at: string | null;
}

export interface IDriveFolders {
  sync_enabled: boolean;
  configured: boolean;
  parent_folder_id: string;
  /** The folders under the brochures folder. Empty with `folders_error` when Pathfinder is unreachable. */
  folders: { id: string; name: string }[];
  folders_error: string;
  listings: IDriveFolderRow[];
}

export const DRIVE_STATE_LABELS: Record<DriveState, string> = {
  sent: "On Drive",
  waiting: "Waiting to send",
  failed: "Send failed",
  off: "Not sending",
};

export const DRIVE_STATE_COLORS: Record<DriveState, string> = {
  sent: "success",
  waiting: "processing",
  failed: "error",
  off: "default",
};

export type ChecklistStatus = "done" | "blocking" | "warning";

/** The office screen that fixes a step; null when only the template can. */
export type ChecklistAction =
  | "copy" | "floor_plan" | "gallery" | "map" | "listing" | "development"
  | "link_development" | "amenity_photos" | null;

/** One thing a template needs from a property. See `readiness.steps`. */
export interface IChecklistStep {
  kind: string;
  label: string;
  status: ChecklistStatus;
  detail: string | null;
  action: ChecklistAction;
  /** For floor plans: the levels (`IDesignLevel` ids) still without one. */
  levels: number[];
  /** For the development slide: the development to complete. */
  development?: number | null;
}

export interface IChecklist {
  /** Every step done. */
  ready: boolean;
  /** Something blocking: the server will refuse to generate. */
  blocked: boolean;
  steps: IChecklistStep[];
}

/** What a slide asks the office to edit. See `slides._sections` in ancana_api. */
/** The destination whose appreciation range a slide prints, edited in place. */
export interface ISlideAppreciation {
  slug: string;
  name: string;
  /** Percent, as decimal strings; null while unset. */
  low: string | null;
  high: string | null;
  /** Properties in the destination, this one included: they share the range. */
  listings: number;
}

export type ISlideSection =
  | { kind: "copy"; roots: string[] }
  | { kind: "development"; development: number | null }
  | { kind: "floor_plan"; index: number; level: number | null; name: string; published: boolean;
      new_level?: INewLevel | null }
  | { kind: "gallery"; slots: string[] }
  | { kind: "amenity_photos" }
  | { kind: "map" }
  | { kind: "payment_plans"; unit: number | null }
  | { kind: "appreciation"; destination: ISlideAppreciation | null }
  | {
      kind: "listing";
      /** Printed from the property's own record; read here, edited in its wizard. */
      fields: { path: string; label: string; source: string; value: string }[];
    };

export interface ISlideProblem {
  kind: string;
  status: Exclude<ChecklistStatus, "done">;
  detail: string;
}

/** One slide of a template, as one property would fill it. */
export interface ISlide {
  number: number;
  /** Guessed from what is on it; the template has no slide titles. */
  label: string;
  status: ChecklistStatus;
  /** Hidden in the template, so never printed. */
  hidden: boolean;
  /** Why this property's brochure leaves the slide out, or null. */
  dropped: string | null;
  sections: ISlideSection[];
  problems: ISlideProblem[];
}

/** `GET presentation-templates/<id>/slides/<listing>/`. */
export interface ISlideOutline extends IChecklist {
  /** Problems that sit on no slide. */
  general: ISlideProblem[];
  /** The PDF the thumbnails are drawn from. */
  preview: {
    source: "brochure" | "template";
    presentation: number | null;
    generated_at: string | null;
    /** Null until the template preview has been built. */
    url: string | null;
    /** Slide number → page in that PDF. JSON keys, so strings. */
    pages: Record<string, number>;
  };
  /** Slide width over height (0.5625 for the portrait brochures); null if unknown. */
  aspect: number | null;
  slides: ISlide[];
}

/**
 * What adding the storey a floor-plan slide asks for would take: present only
 * on the slide for the design's next level. `index` is the new level's order on
 * the design; `homes` is every listing presenting the design, all of which gain
 * the slide.
 */
export interface INewLevel {
  design: number;
  design_name: string;
  index: number;
  homes: number;
}
